import {
  HealthCoachRoleNames,
  HealthCoachType,
} from "src/types/appointment.types";

export const genderOptions: Array<{ value: string; label: string }> = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];

export const STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  APP_VERSION: "app_version",
  BUILD_NUMBER: "build_number",
  PLATFORM: "platform",
  WEB_VERSION: "web_version",
  CLIENT_SOURCE: "client_source",
  MODE: "mode",
  USER_INFO: "user_info",
  QUERY_PARAMS: "query_params",
  THEME_COLORS: "theme_colors",

  CGM_WARMUP_END_DATE: "cgm_warmup_end_date",
  CGM_BOUND_DEVICE: "cgm_bound_device",
  CGM_LAST_GLUCOSE_READING: "cgm_last_glucose_reading",
  CGM_FIRST_BIND_DATE: "cgm_first_bind_date",
  /** RN AsyncStorage parity — last successful hard-sync cursor */
  CGM_LAST_READING_SYNC_TIMESTAMP: "LAST_CGM_READING_SYNC_TIMESTAMP",
};

export const constants = {
  ERROR_MESSAGES: {
    SOMETHING_WENT_WRONG: "Something went wrong!",
  },
  SEARCH_FOOD_ITEM_TITLE: {
    SEARCH_RESULT: "Search Result",
    RECENT_SEARCH: "Recent Search",
  },
};

export const DOCUMENT_REVIEW_STATUS = {
  // Listed as required but nothing uploaded against it yet.
  UPLOAD_PENDING: "Upload Pending",
  UNDER_REVIEW: "Under Review",
  REJECTED: "Rejected",
  APPROVED: "Approved",
};

export const POST_MESSAGE_EVENTS = {
  SHOW_MENU_DRAWER: "SHOW_MENU_DRAWER",
  NAVIGATE_TO_NOTIFICATIONS: "NAVIGATE_TO_NOTIFICATIONS",
  NAVIGATE_TO_WEBVIEW: "NAVIGATE_TO_WEBVIEW",
  SHARE_RECORD_DOCUMENT: "SHARE_RECORD_DOCUMENT",
  OPEN_EXTERNAL_LINK: "OPEN_EXTERNAL_LINK",
  CALL_PHONE: "CALL_PHONE",
  LOGOUT: "LOGOUT",
  FORCE_LOGOUT: "FORCE_LOGOUT",
  USER_DETAILS_UPDATED: "USER_DETAILS_UPDATED",
  NAVIGATE_TO_VISIT_SDK: "NAVIGATE_TO_VISIT_SDK",
  NAVIGATE_TO_DEVICE_SCREEN: "NAVIGATE_TO_DEVICE_SCREEN",
  /** Web → RN: set the native status bar's background color/icon style; data: { backgroundColor, style: "light-content" | "dark-content" } */
  SET_STATUS_BAR_COLOR: "SET_STATUS_BAR_COLOR",
  SYNC_DEVICE: "SYNC_DEVICE",
  /** Web → RN: user tapped Start Pairing; RN should init BCA/Tracky SDK, then expect SYNC_DEVICE with user data */
  START_BCA_PAIRING: "START_BCA_PAIRING",
  /** Web → RN: sync finished (success or error); RN should remove BCA listener / cleanup SDK */
  BCA_SYNC_FINISHED: "BCA_SYNC_FINISHED",
  /** Web → RN: check Bluetooth status; RN responds with BLUETOOTH_ON | BLUETOOTH_OFF | BLUETOOTH_PERMISSION_DENIED */
  CHECK_BLUETOOTH_STATUS: "CHECK_BLUETOOTH_STATUS",
  /** Web → RN: request Bluetooth permission; RN responds with BLUETOOTH_* */
  REQUEST_BLUETOOTH_PERMISSION: "REQUEST_BLUETOOTH_PERMISSION",
  /** Web → RN: user tapped "Turn on Bluetooth"; RN prompts to enable adapter (or opens Settings on iOS) */
  TURN_ON_BLUETOOTH: "TURN_ON_BLUETOOTH",
  /** Native sends to WebView via sendMessageToWebView */
  SYNC_SUCCESS: "SYNC_SUCCESS",
  SYNC_ERROR: "SYNC_ERROR",
  DATA_RECEIVED: "DATA_RECEIVED",
  BLUETOOTH_ON: "BLUETOOTH_ON",
  BLUETOOTH_OFF: "BLUETOOTH_OFF",
  BLUETOOTH_PERMISSION_DENIED: "BLUETOOTH_PERMISSION_DENIED",
  OPEN_SETTINGS: "OPEN_SETTINGS",

  /** Web → RN: check Location status; RN responds with LOCATION_ON | LOCATION_OFF | LOCATION_PERMISSION_DENIED */
  CHECK_LOCATION_STATUS: "CHECK_LOCATION_STATUS",
  /** Web → RN: request Location permission; RN responds with LOCATION_* */
  REQUEST_LOCATION_PERMISSION: "REQUEST_LOCATION_PERMISSION",
  /** Web → RN: user tapped "Turn on Location"; RN prompts to enable location services (or opens Settings) */
  TURN_ON_LOCATION: "TURN_ON_LOCATION",
  LOCATION_ON: "LOCATION_ON",
  LOCATION_OFF: "LOCATION_OFF",
  LOCATION_PERMISSION_DENIED: "LOCATION_PERMISSION_DENIED",

  // --- CGM (Web → RN) ---
  START_CGM_PAIRING: "START_CGM_PAIRING",
  OPEN_CGM_QR_SCANNER: "OPEN_CGM_QR_SCANNER",
  CGM_UNBIND_DEVICE: "CGM_UNBIND_DEVICE",
  CGM_CHECK_STATUS: "CGM_CHECK_STATUS",
  CGM_REQUEST_PERMISSIONS: "CGM_REQUEST_PERMISSIONS",
  CGM_RECONNECT: "CGM_RECONNECT",
  /** Web → RN: open system battery optimization screen for this app (Android) */
  CGM_OPEN_ANDROID_BATTERY_SETTINGS: "CGM_OPEN_ANDROID_BATTERY_SETTINGS",
  /** Web → RN: pull latest readings from device, POST to API */
  CGM_GET_LATEST_READING: "CGM_GET_LATEST_READING",
  CGM_SYNC_MISSING_POINTS: "CGM_SYNC_MISSING_POINTS",
  /** Web → RN: full device history → POST /cgm/logs in batches */
  CGM_HARD_SYNC: "CGM_HARD_SYNC",
  CGM_GENERATE_LOGS: "CGM_GENERATE_LOGS",

  // --- CGM (RN → Web) ---
  CGM_DEVICE_FOUND: "CGM_DEVICE_FOUND",
  CGM_BIND_SUCCESS: "CGM_BIND_SUCCESS",
  CGM_BIND_FAILED: "CGM_BIND_FAILED",
  CGM_CONNECTED: "CGM_CONNECTED",
  CGM_DISCONNECTED: "CGM_DISCONNECTED",
  CGM_WARMUP_STARTED: "CGM_WARMUP_STARTED",
  CGM_GLUCOSE_DATA: "CGM_GLUCOSE_DATA",
  CGM_SENSOR_STATE: "CGM_SENSOR_STATE",
  CGM_STATUS_UPDATE: "CGM_STATUS_UPDATE",
  CGM_ERROR: "CGM_ERROR",
  CGM_HARD_SYNC_SUCCESS: "CGM_HARD_SYNC_SUCCESS",
  CGM_HARD_SYNC_ERROR: "CGM_HARD_SYNC_ERROR",
  CGM_GET_LATEST_READING_SUCCESS: "CGM_GET_LATEST_READING_SUCCESS",
  CGM_GET_LATEST_READING_ERROR: "CGM_GET_LATEST_READING_ERROR",
  CGM_SYNC_MISSING_POINTS_SUCCESS: "CGM_SYNC_MISSING_POINTS_SUCCESS",
  CGM_SYNC_MISSING_POINTS_ERROR: "CGM_SYNC_MISSING_POINTS_ERROR",
  /** RN → Web: Android background sync policy state; payload: { running: boolean; source?: 'foreground_service' | 'work_manager' } */
  CGM_ANDROID_BACKGROUND_SYNC_STATUS: "CGM_ANDROID_BACKGROUND_SYNC_STATUS",
  CGM_LOGS_UPLOADED: "CGM_LOGS_UPLOADED",
  CGM_QR_CANCELLED: "CGM_QR_CANCELLED",
};

/** Age limits for DOB picker (e.g. device details): min/max age in years */
export const DOB_AGE_LIMIT = { MIN: 100, MAX: 18 } as const;

export const APP_LINKS = {
  TERMS_AND_CONDITIONS: "https://shop.goodflip.in/pages/terms-conditions",
  PRIVACY_POLICY: "https://shop.goodflip.in/pages/privacy-policy",
  HEALTH_STORE_LINK: "https://shop.goodflip.in/products",
};

export const STORE_LINKS = {
  GOODFLIP_CARE_APP: {
    APP_STORE: "https://apps.apple.com/in/app/goodflip-care/id6749930876",
    PLAY_STORE:
      "https://play.google.com/store/apps/details?id=com.goodflipcarern&hl=en_IN",
  },
  GOODFLIP_APP: {
    APP_STORE:
      "https://apps.apple.com/in/app/goodflip-formerly-mytatva/id1590299281",
    PLAY_STORE:
      "https://play.google.com/store/apps/details?id=com.mytatva.patient&hl=en_IN",
  },
};

export const EXTERNAL_URLS = {
  CGM_VIDEO_HOW_IT_WORKS: "https://www.youtube.com/watch?v=r5Zemc4R044",
  CGM_VIDEO_HOW_TO_REMOVE: "https://www.youtube.com/watch?v=RK7hxZZFB6I",
  CGM_VIDEO_TROUBLESHOOTING: "https://www.youtube.com/watch?v=ZFGJkpdHwis",
  CGM_VIDEO_HELP_SUPPORT: "https://www.youtube.com/watch?v=JVx1iqXiezo",
  CGM_VIDEO_WARMUP_PERIOD: "https://www.youtube.com/watch?v=MBV_3D7G_vU",
  // TODO: following links needs to be replaced with the actual links
  CGM_HOW_TO_PUT_ON_SENSOR: "https://www.youtube.com/watch?v=O01BBxzxGmE",
  CGM_HOW_TO_REMOVE_SENSOR: "https://www.youtube.com/watch?v=RK7hxZZFB6I",
  CGM_HOW_TO_PUT_ON_TRANSMITTER: "https://www.youtube.com/watch?v=6m3pxl5BK9A",
  CGM_HOW_TO_REMOVE_TRANSMITTER: "https://www.youtube.com/watch?v=RK7hxZZFB6I",
} as const;

export const CHART_TIME_LABELS = [
  "08 PM",
  "04 PM",
  "12 PM",
  "08 AM",
  "04 AM",
  "12 AM",
];

export const HEALTH_COACH_TYPES = {
  NUTRITIONIST: "nutritionist",
  PHYSIOTHERAPIST: "physiotherapist",
  PSYCHOANALYST: "psychoanalyst",
} as const;

export const HEALTH_COACH_ROLE_NAMES = {
  NUTRITIONIST: "Nutritionist",
  PHYSIOTHERAPIST: "Physiotherapist",
  PSYCHOANALYST: "Psychologist (Success Coach)",
} as const;

export const HEALTH_COACH_ROLE_MAPPING: Record<
  HealthCoachRoleNames,
  HealthCoachType
> = {
  [HEALTH_COACH_ROLE_NAMES.NUTRITIONIST]: HEALTH_COACH_TYPES.NUTRITIONIST,
  [HEALTH_COACH_ROLE_NAMES.PHYSIOTHERAPIST]: HEALTH_COACH_TYPES.PHYSIOTHERAPIST,
  [HEALTH_COACH_ROLE_NAMES.PSYCHOANALYST]: HEALTH_COACH_TYPES.PSYCHOANALYST,
};

export const IS_HEALTH_COACH_ENABLED = {
  YES: "Y",
  NO: "N",
} as const;

export const BOOK_APPOINTMENT_PAYMENT_STATUS = {
  COMPLETED: "completed",
  REFUNDED: "refunded",
} as const;

export const PATIENT_PLAN_TYPE = {
  Individual: "Individual",
  Trial: "Trial",
  Subscription: "Subscription",
  Free: "Free",
} as const;

export const APPOINTMENT_SLOT_BOOKING_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
} as const;

export const SCHEDULE_SCREEN_NAMES = {
  BOOK_CONSULTATION: "book_consultation",
  BOOK_LAB_TEST: "book_lab_test",
} as const;
